import React, { FC, Suspense, useEffect, useState } from "react";
import { AxiosResponse, CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { wrapPromise } from "go-core";
import Header from "go-app/components/Header";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import FormatDate from "go-core/components/Formatters/FormatDate";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { ListConfigFilter, ListFilterSource } from "go-list/core/components/Filter/services/types";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { GoListSegmentType, ListConfig } from "go-list/list/services/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { getFilterValuesForDefinedValues } from "../../utils/componentUtils";
import { MessageEventContext } from "./services/context";
import { MessageEventApi } from "./services/types";

interface ListState {
	resource: any;
	organization_name: string;
	getUsers?: (search: string, params: Record<string, any>, options?: Record<string, any>) => Promise<AxiosResponse>;
	companyName?: string;
	taxIdNo?: string;
	organizationId?: number;
	userSearchSelectRequest?: (
		search: string,
		params: Record<string, any>,
		options?: Record<string, any>
	) => Promise<{ id: string; name?: string; label?: string }[]>;
}

interface Props {
	organization_name: string;
	organizationId?: number;
	getUsers?: (search: string, params: Record<string, any>, options?: Record<string, any>) => Promise<AxiosResponse>;
	companyName?: string;
	taxIdNo?: string;
	userSearchSelectRequest?: (
		search: string,
		params: Record<string, any>,
		options?: Record<string, any>
	) => Promise<{ id: string; name?: string; label?: string }[]>;
}

const listName = "MESSAGE_EVENT";
const resourceType = "MESSAGE_EVENT";

const List: FC<ListState> = ({
	resource,
	organization_name,
	organizationId,
	getUsers,
	taxIdNo,
	companyName,
	userSearchSelectRequest,
}) => {
	const location = useLocation();
	const { t } = useTranslation();
	const segmentContextValue = React.useContext(SegmentContext);
	const messageEventService = React.useContext(MessageEventContext);
	const customSourceOptions = messageEventService.getSourceAdditionalOptions();

	if (!resource) return null;
	const data = resource.read();

	const getResourceUrl = (resourceType: string, entityId: number) => {
		const link = messageEventService.getResourceUrl(resourceType, entityId);
		const title = messageEventService.getResourceTitle(resourceType);
		if (!link) return title;
		return (
			<Link to={link} target={"_blank"} rel={"noreferrer"}>
				{title}
			</Link>
		);
	};

	const resourceOptions = messageEventService.resources().reduce((accumulator, value) => {
		return { ...accumulator, [value.id]: value.title };
	}, {});

	let filters: ListConfigFilter[] = [
		{
			id: "resource_id",
			type: "text",
			name: t("lib:go_component.message_events.list.columns.resource_id"),
		},
		{
			id: "resource_type",
			type: "list",
			name: t("lib:go_component.message_events.list.columns.resource_type"),
			options: resourceOptions,
		},
		{
			id: "type",
			name: t("lib:common.word.type"),
			type: "search_select",
			source: {
				request: async () => {
					const res = await messageEventService.api().getTypeOptions();
					return res.map((option) => ({
						id: option,
						label: messageEventService.getMessageEventTypeTitle(option),
					}));
				},
			},
			withoutInfiniteScroll: true,
		},
	];
	const resourceFilters: ListConfigFilter[] = messageEventService
		.resources()
		.filter((x) => x.search !== undefined)
		.map((x) => {
			return {
				id: x.id,
				name: x.title,
				type: "search_select",
				source: {
					request: x.search,
				} as ListFilterSource,
			};
		});

	filters = [...filters, ...resourceFilters];
	getFilterValuesForDefinedValues(
		data,
		messageEventService.getMessageEventTypeTitle.bind(messageEventService),
		"type"
	);

	let config = {
		fields: [
			{
				id: "created_at",
				name: t("lib:common.word.created_at"),
				type: "date",
				render: (item: MessageEventApi) => {
					return FormatDate(item.created_at, undefined, true);
				},
			},
			{
				id: "type",
				name: t("lib:common.word.type"),
				disableSorting: true,
				render: (option: MessageEventApi) => {
					return messageEventService.getMessageEventTypeTitle(option.type);
				},
			},
			{
				id: "resourceType",
				name: t("lib:go_component.message_events.list.columns.resource_type"),
				disableSorting: true,
				render: (option: MessageEventApi) => {
					return option?.resources?.map((resource) =>
						getResourceUrl(resource.resource_type, resource.entity_id)
					);
				},
			},
			{
				id: "source",
				disableSorting: true,
				name: t("lib:common.word.source"),
				type: "list",
				listOptions: {
					AJAX: t(`lib:enums.message_events.source.AJAX`),
					API: t(`lib:enums.message_events.source.API`),
					INTERNAL: t(`lib:enums.message_events.source.INTERNAL`),
					INIT: t(`lib:enums.message_events.source.INIT`),
					MIGRATION: t(`lib:enums.message_events.source.MIGRATION`),
					SYNC: t("lib:enums.message_events.source.SYNC"),
					CSV_IMPORT: t("lib:enums.message_events.source.CSV_IMPORT"),
					...messageEventService.getSourceAdditionalOptions(),
				},
				render: (item: MessageEventApi) => {
					const customItemSource = customSourceOptions?.[item.source];
					if (customItemSource) {
						return customItemSource;
					}

					return t(`lib:enums.message_events.source.${item.source}`);
				},
			},
		],
		filters,
		actions: [
			{
				name: t("lib:common.action.preview"),
				link: (item: MessageEventApi) => {
					return `${location.pathname}/${item.id}`;
				},
			},
		],
		selectedColumns: ["created_at", "user", "type", "source"],
		segments: [
			{
				id: "all",
				name: t("lib:common.word.all"),
				slug: "all",
			} as GoListSegmentType,
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		fetch: async (params: Record<string, any>, sourceToken?: CancelTokenSource) => {
			params.include = "resources,resources.entity_id";
			return (await messageEventService.api().all(params, { cancelToken: sourceToken?.token })).data.data;
		},
		saveSegment: (segment: SegmentType) => {
			return segmentContextValue.save(listName, resourceType, segment);
		},
		exportConfig: {
			title: t("lib:go_component.message_events.list.export_config.title"),
			filename: t("lib:go_component.message_events.list.export_config.filename"),
			organization: organization_name,
			company: companyName,
			taxIdNo,
		},
		doesIdColumnRedirectToPreviewPage: true,
		numberOfStickyColumnsAtTheStart: 1,
	} as ListConfig;

	if (getUsers && config.fields) {
		config.fields = [
			...config.fields,
			{
				id: "user",
				name: t("lib:go_component.message_events.list.columns.username"),
				render: (item: MessageEventApi) => {
					return item.username;
				},
				renderExport: (item: MessageEventApi) => {
					return item.username;
				},
				source: {
					request: (search, params, options) =>
						getUsers(search, params, {
							cancelToken: options?.token,
						}),
				},
			},
		];
	}
	if (getUsers && config.filters) {
		config.filters = [
			...config.filters,
			{
				id: "user_email",
				type: "search_select",
				name: t("lib:go_component.message_events.list.columns.username"),
				source: {
					request: (search, params, options) =>
						getUsers(search, params, {
							cancelToken: options?.token,
						}),
				},
			},
		];
	}

	if (userSearchSelectRequest && config.fields) {
		config.fields.push({
			id: "user",
			name: t("go_component.message_events.list.columns.initiator", { ns: "lib" }),
			type: "search_select",
			render: (item: MessageEventApi) => {
				if (item.terminal?.id && item.terminal?.name && organizationId) {
					return (
						<Link to={`/${organizationId}/settings/terminals/${item.terminal.id}`}>
							{item.terminal.name}
						</Link>
					);
				}
				return item.username;
			},
			renderExport: (item: MessageEventApi) => item.username,
			source: {
				request: async (search: string, params: Record<string, any>, options?: Record<string, any>) => {
					const response = await userSearchSelectRequest(search, params, { cancelToken: options?.token });
					return response.map(({ id, name, label }: { id: string; name?: string; label?: string }) => ({
						id,
						label: name ?? label,
					}));
				},
			},
		});
	}

	const fields = data.fields ? data.fields : [];
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...fields] : fields,
		customFields: fields,
		filterValues: data.filter_values,
	};

	return <ListData config={config} />;
};

const MessageEventList = (props: Props): JSX.Element => {
	const { t } = useTranslation();
	const [resource, setResource] = useState<any>();
	const isMobile = useWindowSize().isMobile;
	const segmentContextValue = React.useContext(SegmentContext);
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleChangeTabTitle(t("lib:go_component.message_events.header.title"));
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType)));
	}, []);

	return (
		<div className="content">
			{!isMobile && <Header title={t("lib:go_component.message_events.header.title")} />}
			<Suspense fallback={<LoadingContainer />}>
				<List
					getUsers={props.getUsers}
					organization_name={props.organization_name}
					resource={resource}
					companyName={props.companyName}
					taxIdNo={props.taxIdNo}
					organizationId={props.organizationId}
					userSearchSelectRequest={props.userSearchSelectRequest}
				/>
			</Suspense>
		</div>
	);
};
export default MessageEventList;
